
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  watch,
  watchEffect,
} from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import Step1 from "@/views/pages/application/recruitment/steps/Step1.vue";
import Step2 from "@/views/pages/application/recruitment/steps/Step2.vue";
import Step3 from "@/views/pages/application/recruitment/steps/Step3.vue";
import Step4 from "@/views/pages/application/recruitment/steps/Step4.vue";
import { StepperComponent } from "@/assets/ts/components";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import * as Yup from "yup";
import { useForm } from "vee-validate";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { Actions } from "@/store/enums/StoreEnums";
import router from "@/router";
import { useToast } from "vue-toastification";
import NoteSection from "@/components/sections/NoteSection.vue";

interface IStep1 {
  strategy: string;
  areChosenFromRecord: string;
  approvedPerson: string;
  hasIdentifiableInformation: string;
  identifiableInformation: string;
  hasAccess: string;
}
interface IStep2 {
  willAccessFromRecord: string;
  accessType: string;
  accessTypeOther: string;
}
interface IStep3 {
  willSubjectsReceiveAnything: string;
  receiveDetail: string;
}

interface CreateAccount extends IStep1, IStep2, IStep3 {}

export default defineComponent({
  name: "kt-vertical-wizard",
  components: {
    Step1,
    Step2,
    Step3,
    Step4,
    NoteSection,
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const toast = useToast();

    const _stepperObj = ref<StepperComponent | null>(null);
    const verticalWizardRef = ref<HTMLElement | null>(null);
    const currentStepIndex = ref(1);
    const jumped = ref(0);
    const previewStep = ref(0);
    const fromJumpled = ref(0);
    const currentRecruitment = computed(() => {
      return store.getters.currentRecruitment;
    });

    let formData = ref<CreateAccount>({
      strategy: currentRecruitment.value.data
        ? currentRecruitment.value.data.strategy
        : "",
      areChosenFromRecord:
        currentRecruitment.value.data &&
        currentRecruitment.value.data.areChosenFromRecord
          ? "1"
          : "0",
      approvedPerson: currentRecruitment.value.data
        ? currentRecruitment.value.data.approvedPerson
        : "",
      hasIdentifiableInformation:
        currentRecruitment.value.data &&
        currentRecruitment.value.data.hasIdentifiableInformation
          ? "1"
          : "",
      identifiableInformation: currentRecruitment.value.data
        ? currentRecruitment.value.data.identifiableInformation
        : "",
      hasAccess:
        currentRecruitment.value.data && currentRecruitment.value.data.hasAccess
          ? "1"
          : "",
      willAccessFromRecord:
        currentRecruitment.value.data &&
        currentRecruitment.value.data.willAccessFromRecord
          ? "1"
          : "",
      accessType: currentRecruitment?.value?.data?.accessType != null && currentRecruitment?.value?.data?.accessType != undefined
        ? currentRecruitment.value.data.accessType
        : "",
      accessTypeOther: currentRecruitment?.value?.data?.accessTypeOther != null && currentRecruitment?.value?.data?.accessTypeOther != undefined
        ? currentRecruitment.value.data.accessTypeOther
        : "",
      willSubjectsReceiveAnything:
        currentRecruitment.value.data &&
        currentRecruitment.value.data.willSubjectsReceiveAnything
          ? "1"
          : "",
      receiveDetail: currentRecruitment.value.data
        ? currentRecruitment.value.data.receiveDetail
        : "",
    });

    onMounted(() => {
      store.dispatch(Actions.GET_IRB_APPLICATION_RECRUITMENT, route.params.id);

      _stepperObj.value = StepperComponent.createInsance(
        verticalWizardRef.value as HTMLElement
      );

      setCurrentPageBreadcrumbs("Vertical", ["Pages", "Wizards"]);
    });

    const createAccountSchema = [
      Yup.object({
        strategy: Yup.string().required().label("Recruitment strategies"),
        areChosenFromRecord: Yup.string(),
        approvedPerson: Yup.string().when("areChosenFromRecord", {
          is: "1",
          then: Yup.string().required().label("Authorization"),
        }),
        hasIdentifiableInformation: Yup.string(),
        identifiableInformation: Yup.string().when(
          "hasIdentifiableInformation",
          {
            is: "1",
            then: Yup.string().required().label("Identiable information"),
          }
        ),
        hasAccess: Yup.string(),
      }),
      Yup.object({
        willAccessFromRecord: Yup.string(),
        accessType: Yup.string().when("willAccessFromRecord", {
          is: "1",
          then: Yup.string().required().label("How will you be accessing subject PHI"),
        }),
        accessTypeOther: Yup.string().when("accessType", {
          is: "Others(Please Specify)",
          then: Yup.string().required().label("Others(Please Specify)"),
        }),
      }),
      Yup.object({
        willSubjectsReceiveAnything: Yup.string(),
        receiveDetail: Yup.string().when("willSubjectsReceiveAnything", {
          is: "1",
          then: Yup.string().required().label("Explanation"),
        }),
      }),
    ];

    const currentSchema = computed(() => {
      return createAccountSchema[currentStepIndex.value - 1];
    });

    const { resetForm, handleSubmit } = useForm<IStep1 | IStep2>({
      validationSchema: currentSchema,
    });

    const totalSteps = computed(() => {
      if (!_stepperObj.value) {
        return;
      }

      return _stepperObj.value.totatStepsNumber;
    });

    resetForm({
      values: {
        ...formData.value,
      },
    });

    const handleStep = handleSubmit((values) => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      formData.value = {
        ...formData.value,
        ...values,
      };

      let data = currentRecruitment.value.data;
      const stepToUpdate =
        jumped.value === 1
          ? previewStep.value
          : fromJumpled.value == 0
          ? currentStepIndex.value - 1
          : currentStepIndex.value;
      if (stepToUpdate === 0) {
        data.strategy = formData.value.strategy;
        data.areChosenFromRecord = formData.value.areChosenFromRecord == "1";
        data.approvedPerson = formData.value.approvedPerson;
        data.hasIdentifiableInformation =
          formData.value.hasIdentifiableInformation == "1";
        data.identifiableInformation = formData.value.identifiableInformation;
        data.hasAccess = formData.value.hasAccess == "1";
      } else if (stepToUpdate === 1) {
        data.willAccessFromRecord = formData.value.willAccessFromRecord == "1";
        data.accessType = formData.value.accessType;
        data.accessTypeOther = formData.value.accessTypeOther;
      } else if (stepToUpdate === 2) {
        data.willSubjectsReceiveAnything =
          formData.value.willSubjectsReceiveAnything == "1";
        data.receiveDetail = formData.value.receiveDetail;
      }
      jumped.value = 0;
      fromJumpled.value = 0;
      if ([0, 1, 2, 3].indexOf(currentStepIndex.value) > -1) {
        const json = JSON.stringify(data);
        store
          .dispatch(Actions.POST_IRB_RECRUITMENT, json)
          .then(() => {
            if (currentStepIndex.value === 3) {
              toast.success("Information Saved", {
                timeout: 2000
              });
              router.push({
                  name: "edit-risk-factor",
                  params: { id: route.params.id },
                });
              
            }
            currentStepIndex.value++;
            if (!_stepperObj.value) {
              return;
            }
            //_stepperObj.value.goNext();
            _stepperObj.value?.goto(currentStepIndex.value);
          })
          .catch(() => {
            toast.error(store.getters.getIrbError, {
                timeout: 2000
              });
          });
      }
    });

    const previousStep = () => {
      if (!_stepperObj.value) {
        return;
      }

      currentStepIndex.value--;

      _stepperObj.value.goPrev();
    };

    const jumpToStep = (step) => {
      jumped.value = 0;
      previewStep.value = currentStepIndex.value - 1;
      fromJumpled.value = 1;
      if (step + 1 !== currentStepIndex.value) {
        jumped.value = 1;
      }
      currentStepIndex.value = step;
      document.getElementById("buttonSubmit")?.click();
      /*if (step !== currentStepIndex.value) {
        console.log("goto step");
        currentStepIndex.value = step;
        _stepperObj.value?.goto(step + 1);
      }*/
    };

    const formSubmit = () => {
      toast.success("Information Saved", {
                timeout: 2000
              });
              window.location.reload();
      
    };

    watchEffect(() => {
      const data = store.getters.currentRecruitment;
      if (data === null || data.data === undefined) return;
      // if (!_stepperObj.value) {
      //   return;
      // }

      // currentStepIndex.value = 2;

      // _stepperObj.value.goto(2);
    });

    watch(
      () => route.params.id,
      async () => {
        await store.dispatch(
          Actions.GET_IRB_APPLICATION_RECRUITMENT,
          route.params.id
        );
      }
    );

    return {
      verticalWizardRef,
      previousStep,
      handleStep,
      formSubmit,
      totalSteps,
      currentStepIndex,
      jumpToStep,
    };
  },
});
